import log from 'loglevel'
import type { Logger, LogLevelDesc } from 'loglevel'

const registeredLoggers = new Map<string, Logger>()

const DEFAULT_LEVEL: LogLevelDesc = import.meta.env.DEV ? 'warn' : 'warn'

export function createLogger(name: string, defaultLevel?: LogLevelDesc): Logger {
  const existing = registeredLoggers.get(name)
  if (existing) {
    return existing
  }

  const logger = log.getLogger(name)
  logger.setDefaultLevel(defaultLevel ?? DEFAULT_LEVEL)
  registeredLoggers.set(name, logger)

  return logger
}

interface VxLogAPI {
  enable: (name: string) => void
  disable: (name: string) => void
  enableAll: () => void
  disableAll: () => void
  list: () => void
  get: (name: string) => Logger | undefined
  setLevel: (name: string, level: LogLevelDesc) => void
}

declare global {
  interface Window {
    VxLog: VxLogAPI
  }
}

export function initVxLog(): void {
  if (typeof window === 'undefined') return

  window.VxLog = {
    enable(name: string) {
      const logger = registeredLoggers.get(name)
      if (logger) {
        logger.setLevel('trace')
        console.log(`[VxLog] Trace enabled for: ${name}`)
      } else {
        console.warn(`[VxLog] Unknown logger: "${name}". Available:`, [...registeredLoggers.keys()])
      }
    },

    disable(name: string) {
      const logger = registeredLoggers.get(name)
      if (logger) {
        logger.setLevel('warn')
        console.log(`[VxLog] Reset to warn for: ${name}`)
      }
    },

    enableAll() {
      registeredLoggers.forEach(logger => logger.setLevel('trace'))
      console.log(`[VxLog] Trace enabled for all ${registeredLoggers.size} loggers`)
    },

    disableAll() {
      registeredLoggers.forEach(logger => logger.setLevel('warn'))
      console.log(`[VxLog] All ${registeredLoggers.size} loggers reset to warn`)
    },

    list() {
      const levelNames = ['TRACE', 'DEBUG', 'INFO', 'WARN', 'ERROR', 'SILENT']
      const entries = [...registeredLoggers.entries()].map(([name, logger]) => ({
        name,
        level: levelNames[logger.getLevel()] ?? 'UNKNOWN',
        debugActive: logger.getLevel() <= log.levels.DEBUG,
      }))
      console.table(entries)
    },

    get(name: string) {
      return registeredLoggers.get(name)
    },

    setLevel(name: string, level: LogLevelDesc) {
      const logger = registeredLoggers.get(name)
      if (logger) {
        logger.setLevel(level)
        const levelNames = ['TRACE', 'DEBUG', 'INFO', 'WARN', 'ERROR', 'SILENT']
        console.log(`[VxLog] ${name} set to ${levelNames[logger.getLevel()]}`)
      } else {
        console.warn(`[VxLog] Unknown logger: "${name}". Available:`, [...registeredLoggers.keys()])
      }
    },
  }
}
